import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import { Clock3, Users, CalendarDays, Timer, Banknote, Hourglass, RotateCcw } from 'lucide-react';

import { StatGrid, ChartCard } from '../../components/dashboard';
import { fetchOTDetailsWithStatus } from '../../store/slices/overtimeSlice';
import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import {
  mapOTRows,
  otDate,
  otWeekday,
  computeOTKpis,
  buildOTByEmployee,
  buildOTByWeekday,
  buildOTByMonth,
  // buildNormalVsOT,
  buildOTTypeDonut,
  buildOTHourBuckets,
  buildOTTrend,
  formatOTHours,
} from '../../utils/overtimeAnalytics';

const FIELD =
  'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

const tooltipStyle = { borderRadius: 8, fontSize: 12, border: '1px solid #e5e7eb' };

// "2026-10" — the API filters OT by month (p_attday=YYYY-MM).
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function OTDashboard() {
  const dispatch = useDispatch();
  const { rows, loading, error } = useSelector((s) => s.overtime);
  const employeeList = useSelector((s) => s.employees?.employees) || [];

  // Emp/GetEmployees — used to show HED_REPORT_NAME for each HOT_EMPLOYEE_ID.
  useEffect(() => {
    if (!employeeList.length) dispatch(fetchAllEmployees());
  }, [dispatch]);

  const employeeById = useMemo(() => {
    const map = new Map();
    employeeList.forEach((e) => {
      const id = String(e.HED_EMPLOYEE_ID ?? '').trim();
      if (id && !map.has(id)) {
        map.set(id, {
          name: String(e.HED_REPORT_NAME || '').trim(),
          serviceNo: String(e.HED_SERVICE_NO || '').trim(),
        });
      }
    });
    return map;
  }, [employeeList]);

  const employeeName = (id) => employeeById.get(String(id).trim())?.name || `Employee #${id}`;

  const [attDay, setAttDay] = useState(currentMonth());   // YYYY-MM

  useEffect(() => {
    if (!attDay) return;   // month input cleared — keep what is shown
    dispatch(fetchOTDetailsWithStatus({ attDay }));
  }, [attDay, dispatch]);

  const mapped = useMemo(
    () =>
      mapOTRows(rows).map((r) => ({
        ...r,
        date: otDate(r.raw?.HOT_OT_DATE),
        weekday: otWeekday(r.raw?.HOT_OT_DATE),
      })),
    [rows]
  );
  const kpis = useMemo(() => computeOTKpis(mapped), [mapped]);
  const typeDonut = useMemo(() => buildOTTypeDonut(mapped), [mapped]);
  // const normalVsOT = useMemo(() => buildNormalVsOT(mapped), [mapped]);
  const byEmployee = useMemo(() => buildOTByEmployee(mapped).slice(0, 8), [mapped]);
  const byWeekday = useMemo(() => buildOTByWeekday(mapped), [mapped]);
  const byMonth = useMemo(() => buildOTByMonth(mapped), [mapped]);
  const buckets = useMemo(() => buildOTHourBuckets(mapped), [mapped]);
  const trend = useMemo(() => buildOTTrend(mapped), [mapped]);

  // GetOTDetails has no normal-hours field, so OT share can't be worked out.
  // const otShare = kpis.totalNormalHours + kpis.totalOTHours > 0
  //   ? ((kpis.totalOTHours / (kpis.totalNormalHours + kpis.totalOTHours)) * 100).toFixed(1)
  //   : '0.0';

  // Real fields from GetOTDetails: HOT_OT_AMOUNT and HOT_IS_APPROVED.
  const totalAmount = useMemo(() => mapped.reduce((s, r) => s + (Number(r.otAmount) || 0), 0), [mapped]);
  const pendingCount = useMemo(() => mapped.filter((r) => !r.isApproved).length, [mapped]);

  // ── KPI cards — this page decides what to show; StatGrid decides how it looks
  const stats = [
    { label: 'Total OT Hours', value: kpis.totalOTHours.toFixed(2), sub: formatOTHours(kpis.totalOTHours), icon: Clock3, tone: 'primary' },
    { label: 'OT Records', value: kpis.records, sub: 'Overtime entries', icon: Timer, tone: 'blue' },
    { label: 'Employees', value: kpis.employees, sub: 'With OT logged', icon: Users, tone: 'violet' },
    { label: 'OT Days', value: kpis.otDays, sub: 'Distinct dates', icon: CalendarDays, tone: 'amber' },
    { label: 'OT Amount', value: `Rs. ${totalAmount.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sub: 'Total overtime cost', icon: Banknote, tone: 'emerald' },
    { label: 'Pending Approval', value: pendingCount, sub: `${kpis.records - pendingCount} approved`, icon: Hourglass, tone: 'indigo' },
    
    // { label: 'OT Share', value: `${otShare}%`, sub: 'Of total hours worked', icon: TrendingUp, tone: 'emerald' },
    // { label: 'Overnight', value: kpis.overnightCount, sub: 'Crossing midnight', icon: Moon, tone: 'indigo' },
  ];

  const noData = mapped.length === 0;

  return (
    <div className="p-6 space-y-4">
      {/* ── Filters ────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <span className={LABEL}>OT Month</span>
            <input
              type="month"
              value={attDay}
              onChange={(e) => setAttDay(e.target.value)}
              className={FIELD}
            />
          </div>
          {/* Shown only when another month is selected — jumps back to this month. */}
          {attDay !== currentMonth() && (
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => setAttDay(currentMonth())}
                disabled={loading}
                className="flex items-center gap-2 h-9 px-3 bg-primary-50 text-primary-700 border border-primary-200 rounded-lg text-xs font-semibold hover:bg-primary-100 transition-colors disabled:opacity-50"
              >
                <RotateCcw size={14} />
                Current Month
              </button>
            </div>
          )}
          {loading && (
            <div className="flex items-end pb-2.5 text-xs text-gray-400">Loading...</div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {loading ? (
        <div className="py-24 text-center text-sm text-gray-400">Loading overtime data...</div>
      ) : (
        <>
          {/* ── KPI cards ─────────────────────────────────────── */}
          <StatGrid stats={stats} columns={6} />

          {/* ── Row 1: monthly trend + type donut + normal vs OT ─ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <ChartCard title="OT Hours by Month" subtitle="Total overtime logged per calendar month" empty={noData}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={byMonth} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} h`, 'OT Hours']} />
                    <Bar dataKey="hours" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <ChartCard title="OT by Type" subtitle="Share of OT hours" empty={noData}>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={typeDonut} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
                    {typeDonut.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} h`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2 text-[11px] text-gray-600">
                {typeDonut.map((d) => (
                  <span key={d.type} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                    {d.name} <span className="font-semibold text-gray-800">{d.value}h</span>
                  </span>
                ))}
              </div>
            </ChartCard>

            {/* GetOTDetails has no normal-hours field — card commented out.
            <ChartCard title="Normal vs OT Hours" subtitle={`Overtime is ${otShare}% of total`} empty={noData}>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={normalVsOT} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
                    {normalVsOT.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} h`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2 text-[11px] text-gray-600">
                {normalVsOT.map((d) => (
                  <span key={d.name} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                    {d.name} <span className="font-semibold text-gray-800">{d.value}h</span>
                  </span>
                ))}
              </div>
            </ChartCard>
            */}
          </div>

          {/* ── Row 2: top employees + weekday + buckets ────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title="Top Employees by OT Hours" subtitle="Highest overtime first" empty={noData}>
              {byEmployee.length ? (
                <div className="space-y-3.5">
                  {byEmployee.map((e, i) => {
                    const max = Math.max(...byEmployee.map((x) => x.hours), 1);
                    const isTop = i < 3;
                    return (
                      <div key={e.employeeId} className="flex items-center gap-3">
                        <span
                          className={`w-6 h-6 shrink-0 rounded-full text-white text-[11px] font-bold flex items-center justify-center ${
                            isTop ? 'bg-primary-500' : 'bg-gray-300'
                          }`}
                        >
                          {i + 1}
                        </span>
                        <span
                          className={`w-32 shrink-0 text-xs truncate ${
                            isTop ? 'text-primary-600 font-medium' : 'text-gray-600'
                          }`}
                          title={(() => {
                            const emp = employeeById.get(String(e.employeeId).trim());
                            return emp?.serviceNo ? `${employeeName(e.employeeId)} (${emp.serviceNo})` : employeeName(e.employeeId);
                          })()}
                        >
                          {employeeName(e.employeeId)}
                        </span>
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full relative">
                          <div
                            className="absolute inset-y-0 left-0 bg-primary-500 rounded-full"
                            style={{ width: `${(e.hours / max) * 100}%` }}
                          />
                        </div>
                        <span className="w-14 text-right text-xs font-semibold text-gray-700 tabular-nums">
                          {e.hours.toFixed(2)}h
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-[230px] flex items-center justify-center text-gray-400 text-sm">No data</div>
              )}
            </ChartCard>

            <ChartCard title="OT by Day of Week" subtitle="Which days overtime concentrates on" empty={noData}>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={byWeekday} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="short" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} h`, 'OT Hours']} />
                  <Bar dataKey="hours" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={30} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="OT Length Distribution" subtitle="How long each OT stint runs" empty={noData}>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={buckets} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Records']} />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={34} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* ── Row 3: daily trend ─────────────────────────────── */}
          <ChartCard title="Daily OT Trend" subtitle="OT hours per attendance date" empty={noData}>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trend} margin={{ top: 8, right: 16, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v) => [`${v} h`, 'OT Hours']}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.date || label}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="hours" name="OT Hours" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}
    </div>
  );
}